"use client"

import type { FunctionComponent } from "react"

import { MainButtonBlock } from "@/features/eventDetails/components/RegistrationDetails/MainButtonBlock"
import { RegistrationDetailsPlaceholder } from "@/features/eventDetails/components/RegistrationDetails/RegistrationDetailsPlaceholder"
import { RegistrationReminder } from "@/features/eventDetails/components/RegistrationDetails/RegistrationReminder"
import { SpotsLeft } from "@/features/eventDetails/components/RegistrationDetails/SpotsLeft"
import { StatusConfirmed } from "@/features/eventDetails/components/RegistrationDetails/StatusConfirmed"
import { StatusDeclined } from "@/features/eventDetails/components/RegistrationDetails/StatusDeclined"
import { StatusUserNotAvailable } from "@/features/eventDetails/components/RegistrationDetails/StatusUserNotAvailable"
import { StatusWarning } from "@/features/eventDetails/components/RegistrationDetails/StatusWarning"
import { UnregistrationReminder } from "@/features/eventDetails/components/RegistrationDetails/UnregistrationReminder"
import { Unsubscribe } from "@/features/eventDetails/components/RegistrationDetails/Unsubscribe"
import {
  REGISTRATION_STATUS_DECLINED,
  REGISTRATION_STATUS_INCOMPLETE,
  REGISTRATION_STATUS_NOT_ALLOWED,
  REGISTRATION_STATUS_NOT_SUBSCRIBED,
  REGISTRATION_STATUS_ON_WAITING_LIST,
  REGISTRATION_STATUS_PENDING_APPROVAL,
  REGISTRATION_STATUS_SUBSCRIBED,
  REGISTRATION_STATUS_UNSUBSCRIBED,
  REGISTRATION_STATUS_USER_NOT_AVAILABLE,
} from "@/modules/events/constants"
import { useRegistrationStatus } from "@/modules/events/hooks/useRegistrationStatus"
import { useTranslation } from "@/modules/i18n/components/useTranslation"
import { useLocale } from "@/modules/locales/useLocale"

export type RegistrationDetailsProps = {
  id: string
}

export const RegistrationDetails: FunctionComponent<RegistrationDetailsProps> = ({ id }) => {
  const locale = useLocale()
  // Preload of the translation dictionaries for all the registration components & modals
  const { ready } = useTranslation(
    ["event_details", "shared_upload", "shared_action", "shared_modal", "shared_error", "shared_select_input"],
    true
  )
  const { registrationStatus, isErrorRequestStatus, isLoadingRequestStatus } = useRegistrationStatus({
    eventId: id,
    locale,
  })

  const isRegistrationDetailsPlaceholderDisplayed =
    typeof registrationStatus === "undefined" || isErrorRequestStatus || isLoadingRequestStatus

  if (isRegistrationDetailsPlaceholderDisplayed || !ready) {
    return <RegistrationDetailsPlaceholder />
  }

  const { status, remainingSpots } = registrationStatus

  const isNotSubscribed = status === REGISTRATION_STATUS_NOT_SUBSCRIBED || status === REGISTRATION_STATUS_UNSUBSCRIBED
  const isConfirmed = status === REGISTRATION_STATUS_SUBSCRIBED
  const isIncomplete = status === REGISTRATION_STATUS_INCOMPLETE
  const isPendingApproval = status === REGISTRATION_STATUS_PENDING_APPROVAL
  const isOnWaitingList = status === REGISTRATION_STATUS_ON_WAITING_LIST
  const isDeclined = status === REGISTRATION_STATUS_DECLINED
  const hasButtonBlock = !isPendingApproval && !isOnWaitingList && !isDeclined
  const hasSpotsLeft = isNotSubscribed && remainingSpots !== undefined && remainingSpots > 0
  const hasReminder =
    ((isNotSubscribed || status === REGISTRATION_STATUS_NOT_ALLOWED) && remainingSpots === undefined) ||
    !!(remainingSpots !== undefined && remainingSpots > 0)
  const isUserNotAvailable = registrationStatus.status === REGISTRATION_STATUS_USER_NOT_AVAILABLE

  return (
    <>
      {hasButtonBlock && <MainButtonBlock registrationStatus={registrationStatus} />}
      {isConfirmed && <StatusConfirmed registrationStatus={registrationStatus} />}
      {isIncomplete && <StatusWarning status="incomplete" />}
      {isPendingApproval && <StatusWarning status="pending" />}
      {isOnWaitingList && <StatusWarning status="waitingList" />}
      {isDeclined && <StatusDeclined />}
      {(isOnWaitingList || isConfirmed) && <Unsubscribe id={id} />}
      {hasReminder && <RegistrationReminder registrationStatus={registrationStatus} />}
      {isConfirmed && <UnregistrationReminder registrationStatus={registrationStatus} />}
      {isUserNotAvailable && <StatusUserNotAvailable />}
      {hasSpotsLeft && <SpotsLeft counter={remainingSpots} />}
    </>
  )
}
